import type { Chapter, ItineraryDay } from '@travel-planner/shared/schemas';

// Legacy palette (lib/utils/chapters.ts CHAPTER_COLORS) — colors after Black
// kept verbatim so re-saves from the new app don't shift existing chapter
// swatches. Black leads as the default sober/minimal choice.
export const CHAPTER_COLORS: ReadonlyArray<string> = [
  '#000000',
  '#3B82F6',
  '#F97316',
  '#10B981',
  '#8B5CF6',
  '#EC4899',
  '#F59E0B',
  '#06B6D4',
  '#EF4444',
];

// `sections` is typed as `z.unknown()` at the schema boundary; widen
// at the FE consumer instead of forcing every caller through `as`.
export const readChapters = (raw: unknown): Chapter[] => {
  if (!Array.isArray(raw)) return [];
  return (raw as Chapter[]).slice().sort((a, b) => a.order - b.order);
};

export const getChapterById = (
  chapters: Chapter[],
  id: string | null | undefined
): Chapter | null => {
  if (!id) return null;
  return chapters.find((c) => c.id === id) ?? null;
};

// Window-match a day to a chapter. Day `date` is an ISO string in
// the same `YYYY-MM-DD` UTC wall-clock the chapter `startDate` /
// `endDate` use, so a lexical comparison is correct. Returns null
// for undated days (legacy parity — chapters can't tag a `null`
// date).
export const getChapterForDate = (
  date: string | null,
  chapters: Chapter[]
): Chapter | null => {
  if (!date) return null;
  const day = date.slice(0, 10);
  for (const c of chapters) {
    if (c.startDate <= day && day <= c.endDate) return c;
  }
  return null;
};

// Filter the day list to those that sit inside the active chapter's
// window. Days without a `date` (undated itinerary) are kept only
// when the chapter has no window restriction (start/end identical to
// the itinerary anchor) — for now we drop them so the filter is
// unambiguous; undated chapters land in a follow-up.
export const filterDaysByChapter = (
  days: ItineraryDay[],
  chapter: Chapter | null
): ItineraryDay[] => {
  if (!chapter) return days;
  return days.filter((d) => {
    if (!d.date) return false;
    const day = d.date.slice(0, 10);
    return chapter.startDate <= day && day <= chapter.endDate;
  });
};

// Auto-pick the next palette colour when the consultant adds a chapter.
// Cycles through `CHAPTER_COLORS` by current length, matching legacy
// `ChapterManager.tsx` add-handler behaviour.
export const nextChapterColor = (count: number): string => {
  const palette = CHAPTER_COLORS;
  return palette[count % palette.length] ?? palette[0] ?? '#3B82F6';
};

// Day-number ↔ ISO-date helpers for UNDATED itineraries. Their chapters
// still store real dates (anchored to a year-2000 sentinel, legacy
// parity), which are meaningless to a consultant — the editor surfaces
// them as "Day N" relative to the itinerary's start instead.
const MS_PER_DAY = 86_400_000;

const parseUtcDay = (date: string): number =>
  Date.parse(`${date.slice(0, 10)}T00:00:00.000Z`);

// 1-based day number of `date` within an itinerary starting on `start`.
// Clamped to >= 1 so a malformed / earlier date never yields Day 0.
export const dayNumberFromDate = (date: string, start: string): number => {
  const anchor = parseUtcDay(start);
  const target = parseUtcDay(date);
  if (Number.isNaN(anchor) || Number.isNaN(target)) return 1;
  return Math.max(1, Math.round((target - anchor) / MS_PER_DAY) + 1);
};

// Inverse: the ISO date (YYYY-MM-DD) of 1-based `dayNumber` from `start`.
export const dateFromDayNumber = (dayNumber: number, start: string): string => {
  const d = new Date(`${start.slice(0, 10)}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + Math.max(0, dayNumber - 1));
  return d.toISOString().slice(0, 10);
};

// Inclusive day span between two anchor dates (Day 1..Day N). Falls back
// to 1 when either bound is missing / invalid.
export const dayCountBetween = (start: string, end: string): number => {
  const anchor = parseUtcDay(start);
  const last = parseUtcDay(end);
  if (Number.isNaN(anchor) || Number.isNaN(last)) return 1;
  return Math.max(1, Math.round((last - anchor) / MS_PER_DAY) + 1);
};

// Re-stamp the `order` field on every chapter to its array index. Run
// before mutation so up/down swaps + deletes can't leak duplicate or
// stale orders to the BE. Generic so the editor's draft rows (which
// carry FE-only fields like `travellerMode`) round-trip without losing
// their extras.
export const renumberOrders = <T extends Pick<Chapter, 'order'>>(
  chapters: T[]
): T[] => chapters.map((c, i) => ({ ...c, order: i }));
